import type { CursorPage } from '@/hooks/use-cursor-select';
import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';

import { reportOptionPageSchema } from './schemas';
import type { ReportOption, ReportOptionParams } from './types';

/** One page of a Reports filter dropdown, flattened into the shape `useCursorSelect` wants.
 *  `apiClient.getList` splits the envelope into `{ data, meta }`; `meta` is defaulted because a
 *  single-page response may omit it (the shipped `listReplacementCustomers` does the same). */
async function fetchOptionPage(
  endpoint: string,
  params: Record<string, unknown>,
): Promise<CursorPage<ReportOption>> {
  const page = await apiClient.getList(endpoint, { params });
  const { data, meta } = reportOptionPageSchema.parse({
    ...page,
    meta: page.meta ?? { hasNextPage: false },
  });
  return { data, nextCursor: meta.nextCursor, hasNextPage: meta.hasNextPage };
}

const cursored = ({ search, cursor, limit }: ReportOptionParams) => ({
  search,
  limit,
  ...(cursor ? { cursor } : {}),
});

/** Customers who own at least one product (replaced units excluded) — `hasProduct=true`, added to
 *  the shared customers meta endpoint for Reports. Shared by the Products, Device Activity and
 *  Subscriptions tabs. Order Summary wants `hasOrder=true` instead, which `features/orders`'
 *  `listOrderCustomers` already ships.
 *
 *  Known imprecision the backend spec accepts: this asks "owns any product" while Device Activity
 *  is subscribable-only, so a customer owning only components is offered and yields an empty grid.
 *  No third flag exists. */
export const listReportCustomers = (params: ReportOptionParams) =>
  fetchOptionPage(endpoints.reports.meta.customers, { ...cursored(params), hasProduct: true });

/** Catalog **devices** (models) with at least one subscribable, owned unit — `isOwned=true`. This
 *  is why selecting a device filters by model rather than by owned unit (README AC15). */
export const listReportDevices = (params: ReportOptionParams) =>
  fetchOptionPage(endpoints.reports.meta.devices, { ...cursored(params), isOwned: true });

/** Categories with at least one subscribable, owned device — `isOwned=true`. */
export const listReportCategories = (params: ReportOptionParams) =>
  fetchOptionPage(endpoints.reports.meta.categories, { ...cursored(params), isOwned: true });
